/**
 * IssueTreeBlock - IRAC-style issue tree for legal/argument analysis
 */

import React, { useState } from 'react'
import { IssueTreeBlock as IssueTreeBlockType, IssueNode } from '../../../../lib/cockpit/schemas'

interface IssueTreeBlockProps {
  data: IssueTreeBlockType['data']
  blockId: string
  compact?: boolean
}

const NODE_TYPE_STYLES = {
  issue: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    icon: '❓',
    label: 'Issue',
  },
  rule: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    border: 'border-purple-200 dark:border-purple-800',
    icon: '📜',
    label: 'Rule',
  },
  application: {
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    border: 'border-orange-200 dark:border-orange-800',
    icon: '⚖️',
    label: 'Application',
  },
  conclusion: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    icon: '✓',
    label: 'Conclusion',
  },
  sub_issue: {
    bg: 'bg-gray-50 dark:bg-gray-800/50',
    border: 'border-gray-200 dark:border-gray-700',
    icon: '•',
    label: 'Sub-Issue',
  },
}

const STRENGTH_COLORS = {
  strong: 'text-green-600 dark:text-green-400',
  moderate: 'text-yellow-600 dark:text-yellow-400',
  weak: 'text-red-600 dark:text-red-400',
}

interface IssueNodeComponentProps {
  node: IssueNode
  depth: number
  compact?: boolean
}

function IssueNodeComponent({ node, depth, compact }: IssueNodeComponentProps) {
  const [expanded, setExpanded] = useState(depth < 2)
  const style = NODE_TYPE_STYLES[node.type as keyof typeof NODE_TYPE_STYLES]
  const hasChildren = node.children && node.children.length > 0

  if (compact && depth > 0) {
    return null
  }

  return (
    <div className={`${depth > 0 ? 'ml-6 mt-2' : ''}`}>
      <div className={`
        rounded-lg border ${style.bg} ${style.border} p-3
        ${hasChildren ? 'cursor-pointer' : ''}
      `}
        onClick={hasChildren ? () => setExpanded(!expanded) : undefined}
      >
        <div className="flex items-start gap-2">
          {hasChildren && (
            <svg
              className={`w-4 h-4 mt-0.5 text-gray-500 transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span>{style.icon}</span>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {style.label}
              </span>
              {node.strength && (
                <span className={`text-xs font-medium ${STRENGTH_COLORS[node.strength as keyof typeof STRENGTH_COLORS]}`}>
                  ({node.strength})
                </span>
              )}
            </div>
            <p className="text-gray-800 dark:text-gray-200 text-sm leading-relaxed">
              {node.content}
            </p>
          </div>
        </div>
      </div>
      
      {expanded && hasChildren && (
        <div className="border-l-2 border-gray-200 dark:border-gray-700">
          {node.children!.map((child: IssueNode, index: number) => (
            <IssueNodeComponent
              key={`${child.id}-${index}`}
              node={child}
              depth={depth + 1}
              compact={compact}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function IssueTreeBlock({ data, blockId, compact }: IssueTreeBlockProps) {
  return (
    <div className="space-y-3">
      <IssueNodeComponent node={data.root} depth={0} compact={compact} />
      
      {data.verdict && (
        <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-medium text-green-800 dark:text-green-300">
              Verdict
            </span>
          </div>
          <p className="text-green-900 dark:text-green-100 font-medium">
            {data.verdict}
          </p>
        </div>
      )}
    </div>
  )
}

export default IssueTreeBlock



